import React, { useMemo } from 'react'
import { Drawer, Flex, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { orderBy } from 'lodash-es'
import { DangerBooleanIcon } from '@/common/components/BooleanIcon.tsx'
import {
  EmployeeDataSection,
  isSensitiveEmployeeDataSection,
} from '@/common/types/employee-data-permission.def.ts'
import { SECTION_GROUP_LABELS } from '@/pages/portal-configuration/components/SectionGroupTags.tsx'

type Props = {
  open: boolean
  onClose: () => void
  /**
   * The backend's live sensitive-section list. Optional: when absent the drawer falls back to the
   * local constant, which is why `isSensitiveEmployeeDataSection` takes it as an override rather
   * than the drawer reimplementing the check.
   */
  sensitiveSections?: string[]
}

type SectionRow = {
  section: string
  isSensitive: boolean
}

/**
 * Reference list of every employee data section and whether it is sensitive.
 *
 * The section names come from the local `EmployeeDataSection` map because that is the only place
 * the full list exists client-side — the access claim response carries just the sensitive subset.
 * The sensitivity flag prefers the backend's list so the reference cannot claim something is
 * harmless that the gates treat as sensitive.
 */
export const SectionReferenceDrawer: React.FC<Props> = ({ open, onClose, sensitiveSections }) => {
  const rows = useMemo<SectionRow[]>(() => {
    const sections = Object.values(EmployeeDataSection)
      // A sentinel for unmapped data, never a grantable section.
      .filter(section => section !== EmployeeDataSection.Unknown)

    return orderBy(
      sections.map(section => ({
        section,
        isSensitive: sensitiveSections
          ? isSensitiveEmployeeDataSection(section, sensitiveSections)
          : isSensitiveEmployeeDataSection(section),
      })),
      // Sensitive first, then alphabetical - the sensitive ones are what a reader came for.
      [row => (row.isSensitive ? 0 : 1), row => row.section],
      ['asc', 'asc'],
    )
  }, [sensitiveSections])

  const sensitiveCount = rows.filter(row => row.isSensitive).length

  const columns: ColumnsType<SectionRow> = [
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      sorter: (a, b) => a.section.localeCompare(b.section),
    },
    {
      title: 'Group',
      key: 'group',
      width: 140,
      filters: [
        { text: SECTION_GROUP_LABELS.sensitive, value: true },
        { text: SECTION_GROUP_LABELS.base, value: false },
      ],
      onFilter: (value, row) => row.isSensitive === value,
      render: (_, row) => (
        row.isSensitive
          ? <Tag color="red" className="m-0">{SECTION_GROUP_LABELS.sensitive}</Tag>
          : <Tag color="blue" className="m-0">{SECTION_GROUP_LABELS.base}</Tag>
      ),
    },
    {
      title: 'Sensitive',
      key: 'isSensitive',
      width: 100,
      align: 'center',
      render: (_, row) => <DangerBooleanIcon value={row.isSensitive} />,
    },
  ]

  return (
    <Drawer
      title="Employee Data Sections"
      open={open}
      onClose={onClose}
      width={560}
    >
      <Flex vertical gap="middle">
        <Typography.Text type="secondary">
          {rows.length} sections, {sensitiveCount} of them sensitive.
        </Typography.Text>
        <Table<SectionRow>
          rowKey="section"
          size="small"
          pagination={false}
          dataSource={rows}
          columns={columns}
        />
      </Flex>
    </Drawer>
  )
}
